<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed inset-0 z-50 overflow-y-auto" @close="close">
      <div class="flex min-h-screen items-center justify-center px-4 py-4 text-center sm:p-4">
        <TransitionChild
          as="template"
          enter="ease-out duration-200"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-150"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-black bg-opacity-40 backdrop-soft transition-opacity" />
        </TransitionChild>

        <TransitionChild
          as="template"
          enter="ease-out duration-200"
          enter-from="opacity-0 translate-y-3 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-150"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-3 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="relative w-full max-w-5xl overflow-hidden border border-slate-700 bg-slate-800 text-left shadow-2xl"
            style="border-radius: 28px; max-height: calc(100vh - 32px); display: flex; flex-direction: column;"
          >
            <!-- HEADER -->
            <div class="shrink-0 border-b border-slate-700 px-5 py-5 sm:px-6">
              <div class="flex items-start justify-between gap-4">
                <div class="min-w-0 flex-1">
                  <div class="flex flex-wrap items-center gap-2">
                    <div class="truncate text-lg font-semibold tracking-tight text-white">
                      {{ invoice?.invoiceName || "Szczegóły faktury" }}
                    </div>

                    <div
                      v-if="invoice?.ksef?.ksefNumber"
                      class="inline-flex items-center gap-2 rounded-2xl border border-green-500 border-opacity-20 bg-green-500 bg-opacity-10 px-3 py-1 font-semibold text-green-200"
                      style="font-size: 11px;"
                    >
                      <CheckBadgeIcon class="h-4 w-4" />
                      KSeF
                    </div>

                    <div
                      v-if="invoice?.invoiceDeliveryDate"
                      class="inline-flex items-center gap-2 rounded-2xl border border-slate-600 bg-slate-700 px-3 py-1 font-semibold text-slate-200"
                      style="font-size: 11px;"
                    >
                      <CalendarDaysIcon class="h-4 w-4" />
                      Wystawiono: {{ formatDate(invoice.invoiceDeliveryDate) }}
                    </div>
                  </div>

                  <div class="mt-1 truncate text-xs text-slate-400">
                    Podgląd pozycji faktury, danych stron, płatności i danych KSeF.
                  </div>
                </div>

                <div class="flex shrink-0 items-center gap-2">
                  <!-- PDF EXPORT BUTTON -->
                  <button
                    type="button"
                    class="iconBtn"
                    title="Pobierz zestawienie PDF"
                    :disabled="pdfGenerating"
                    @click="exportPdf"
                  >
                    <span v-if="pdfGenerating" class="pdfSpinner" />
                    <DocumentArrowDownIcon v-else class="h-4 w-4 text-slate-200" />
                  </button>

                  <button
                    v-if="invoice?.ksef?.invoiceQr"
                    type="button"
                    class="iconBtn"
                    title="Kopiuj link"
                    @click="copyInvoiceQr(invoice.ksef.invoiceQr)"
                  >
                    <LinkIcon class="h-4 w-4 text-slate-200" />
                  </button>

                  <button
                    type="button"
                    class="iconBtn"
                    aria-label="Zamknij"
                    @click="close"
                  >
                    <XMarkIcon class="h-4 w-4 text-slate-200" />
                  </button>
                </div>
              </div>
            </div>

            <!-- BODY -->
            <div class="min-h-0 flex-1 overflow-y-auto">
              <div class="space-y-4 p-5 sm:p-6">
                <!-- KSEF CARD -->
                <div
                  v-if="invoice?.ksef?.invoiceQr"
                  class="border border-slate-700 bg-slate-700 bg-opacity-40 p-4 shadow-lg sm:p-5"
                  style="border-radius: 26px;"
                >
                  <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div class="flex min-w-0 flex-col gap-4 md:flex-row md:items-center">
                      <!-- QR -->
                      <div class="shrink-0 qrWrap">
                        <div class="border border-slate-200 bg-white p-3 shadow-md qrInner" style="border-radius: 22px;">
                          <QrcodeVue :value="invoice.ksef.invoiceQr" :size="140" level="M" render-as="svg" />
                        </div>
                      </div>

                      <!-- CONTENT -->
                      <div class="min-w-0 flex-1">
                        <div
                          class="inline-flex items-center gap-2 rounded-2xl border border-green-500 border-opacity-20 bg-green-500 bg-opacity-10 px-3 py-1.5 font-semibold text-green-200"
                          style="font-size: 11px;"
                        >
                          <CheckBadgeIcon class="h-4 w-4" />
                          Faktura dostępna w KSeF
                        </div>

                        <div class="mt-3 text-lg font-semibold tracking-tight text-white">
                          Dokument został poprawnie zarejestrowany
                        </div>

                        <div class="mt-1 text-sm leading-6 text-slate-300">
                          Zeskanuj kod QR lub użyj bezpośredniego linku.
                        </div>

                        <div class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div class="softBox">
                            <div class="softBoxLabel">Numer KSeF</div>
                            <div class="softBoxValue font-mono break-all">{{ invoice.ksef.ksefNumber || "—" }}</div>
                          </div>
                          <div class="softBox">
                            <div class="softBoxLabel">Data wystawienia</div>
                            <div class="softBoxValue">{{ formatDate(invoice.invoiceDeliveryDate) }}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- ACTIONS -->
                    <div class="flex shrink-0 flex-col gap-2 sm:flex-row xl:flex-col actionCol actionButtons">
                      <a :href="invoice.ksef.invoiceQr" target="_blank" rel="noopener noreferrer" class="primaryBtn">
                        <ArrowTopRightOnSquareIcon class="h-4 w-4 btnIcon" />
                        <span class="btnLabel">Otwórz w KSeF</span>
                      </a>
                      <button type="button" class="btn" @click="copyKsefNumber(invoice.ksef.ksefNumber)">
                        <DocumentDuplicateIcon class="h-4 w-4 btnIcon" />
                        <span class="btnLabel">Kopiuj numer</span>
                      </button>
                      <button type="button" class="btn" @click="copyInvoiceQr(invoice.ksef.invoiceQr)">
                        <LinkIcon class="h-4 w-4 btnIcon" />
                        <span class="btnLabel">Kopiuj link</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- DANE KOREKTY — widoczne tylko dla faktur korygujących -->
                <div v-if="isCorrection" class="border border-amber-600 border-opacity-40 bg-amber-900 bg-opacity-20 shadow-lg" style="border-radius: 26px;">
                  <div class="flex items-center justify-between gap-3 border-b border-amber-600 border-opacity-30 px-5 py-4">
                    <div class="text-sm font-semibold text-amber-100">Dane korekty (KSeF FA(3))</div>
                    <span class="inline-flex items-center gap-2 rounded-xl border border-amber-500 border-opacity-40 bg-amber-900 bg-opacity-30 px-3 py-1.5 font-semibold text-amber-200" style="font-size: 11px;">
                      KOR
                    </span>
                  </div>
                  <div class="p-5 space-y-4">
                    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div class="softBox">
                        <div class="softBoxLabel">Faktura korygowana</div>
                        <div class="softBoxValue">{{ correctionMeta.invoiceName || '—' }}</div>
                      </div>
                      <div class="softBox">
                        <div class="softBoxLabel">Data wystawienia faktury korygowanej</div>
                        <div class="softBoxValue">{{ correctionMeta.issueDate || '—' }}</div>
                      </div>
                      <div class="softBox md:col-span-2">
                        <div class="softBoxLabel">Numer KSeF faktury korygowanej</div>
                        <div v-if="correctionMeta.loadingPrimary" class="softBoxValue text-slate-400">Wczytywanie…</div>
                        <div v-else-if="correctionMeta.primaryKsefNumber" class="softBoxValue font-mono break-all">{{ correctionMeta.primaryKsefNumber }}</div>
                        <div v-else class="softBoxValue text-red-300">Brak — korekta nie zostanie wysłana do KSeF dopóki faktura pierwotna nie będzie miała numeru KSeF.</div>
                      </div>
                      <div class="softBox md:col-span-2">
                        <div class="softBoxLabel">Przyczyna korekty</div>
                        <div class="softBoxValue whitespace-pre-wrap">{{ correctionMeta.reason || '—' }}</div>
                      </div>
                    </div>

                    <!-- DANE STRON PRZED KOREKTĄ — tylko jeśli się różnią od aktualnych -->
                    <div v-if="hasBuyerBeforeCorrection || hasSellerBeforeCorrection" class="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div v-if="hasSellerBeforeCorrection" class="softBox">
                        <div class="softBoxLabel">Sprzedawca przed korektą</div>
                        <div class="mt-3 space-y-2">
                          <div class="text-sm font-semibold text-amber-100">{{ sellerBefore.name || '—' }}</div>
                          <div class="space-y-2">
                            <div class="softMiniRow"><div class="softMiniKey">NIP</div><div class="softMiniVal">{{ sellerBefore.nip || '—' }}</div></div>
                            <div class="softMiniRow softMiniRow--top"><div class="softMiniKey">Adres</div><div class="softMiniVal">{{ sellerBefore.address || '—' }}</div></div>
                          </div>
                        </div>
                      </div>
                      <div v-if="hasBuyerBeforeCorrection" class="softBox">
                        <div class="softBoxLabel">Nabywca przed korektą</div>
                        <div class="mt-3 space-y-2">
                          <div class="text-sm font-semibold text-amber-100">{{ buyerBefore.name || '—' }}</div>
                          <div class="space-y-2">
                            <div class="softMiniRow"><div class="softMiniKey">NIP</div><div class="softMiniVal">{{ buyerBefore.nip || '—' }}</div></div>
                            <div class="softMiniRow softMiniRow--top"><div class="softMiniKey">Adres</div><div class="softMiniVal">{{ buyerBefore.address || '—' }}</div></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- DANE STRON -->
                <div class="border border-slate-700 bg-slate-700 bg-opacity-30 shadow-lg" style="border-radius: 26px;">
                  <div class="flex items-center justify-between gap-3 border-b border-slate-700 px-5 py-4">
                    <div class="text-sm font-semibold text-white">{{ isCorrection ? 'Dane stron po korekcie' : 'Dane stron' }}</div>
                    <span class="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-3 py-1.5 font-semibold text-slate-200" style="font-size: 11px;">
                      <BuildingOffice2Icon class="h-4 w-4" />
                      {{ isCorrection ? 'Korekta' : 'Faktura' }}
                    </span>
                  </div>
                  <div class="p-5">
                    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div class="softBox">
                        <div class="softBoxLabel">Sprzedawca</div>
                        <div class="mt-3 space-y-2">
                          <div class="text-sm font-semibold text-white">{{ currentSeller.name }}</div>
                          <div class="space-y-2">
                            <div class="softMiniRow"><div class="softMiniKey">NIP</div><div class="softMiniVal">{{ currentSeller.nip }}</div></div>
                            <div class="softMiniRow softMiniRow--top"><div class="softMiniKey">Adres</div><div class="softMiniVal">{{ currentSeller.address }}</div></div>
                          </div>
                        </div>
                      </div>
                      <div class="softBox">
                        <div class="softBoxLabel">Nabywca</div>
                        <div class="mt-3 space-y-2">
                          <div class="text-sm font-semibold text-white">{{ invoice?.payerName || "—" }}</div>
                          <div class="space-y-2">
                            <div class="softMiniRow"><div class="softMiniKey">NIP</div><div class="softMiniVal">{{ invoice?.payerNIP || "—" }}</div></div>
                            <div class="softMiniRow softMiniRow--top"><div class="softMiniKey">Adres</div><div class="softMiniVal">{{ invoice?.payerAddress || "—" }}</div></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- PAYMENT + SUMMARY -->
                <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div class="border border-slate-700 bg-slate-700 bg-opacity-30 shadow-lg" style="border-radius: 26px;">
                    <div class="flex items-center justify-between gap-3 border-b border-slate-700 px-5 py-4">
                      <div class="text-sm font-semibold text-white">Płatność</div>
                      <span class="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-3 py-1.5 font-semibold text-slate-200" style="font-size: 11px;">
                        <CreditCardIcon class="h-4 w-4" />Rozliczenie
                      </span>
                    </div>
                    <div class="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
                      <div class="softBox"><div class="softBoxLabel">Status płatności</div><div class="softBoxValue">{{ paymentStatusLabel }}</div></div>
                      <div class="softBox"><div class="softBoxLabel">Kwota opłacona</div><div class="softBoxValue">{{ formatMoney(invoice?.paidAmount) }} PLN</div></div>
                      <div class="softBox"><div class="softBoxLabel">Termin płatności</div><div class="softBoxValue">{{ paymentTermLabel }}</div></div>
                      <div class="softBox"><div class="softBoxLabel">Sposób płatności</div><div class="softBoxValue">przelew</div></div>
                    </div>
                    <div class="px-5 pb-5">
                      <div class="softBox">
                        <div class="softBoxLabel">Numer konta</div>
                        <div class="softBoxValue font-mono break-all">42 1020 1068 0000 1102 0371 8830</div>
                        <div class="mt-1 text-slate-400" style="font-size: 11px;">PKO</div>
                      </div>
                    </div>
                  </div>

                  <div class="border border-slate-700 bg-slate-700 bg-opacity-30 shadow-lg summaryCard" style="border-radius: 26px;">
                    <div class="flex items-center justify-between gap-3 border-b border-slate-700 px-5 py-4">
                      <div class="text-sm font-semibold text-white">Podsumowanie</div>
                      <span class="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-3 py-1.5 font-semibold text-slate-200" style="font-size: 11px;">
                        <BanknotesIcon class="h-4 w-4" />Kwoty
                      </span>
                    </div>
                    <div class="space-y-3 p-5 summaryCardBody">
                      <div class="softBox summaryStretchBox"><div class="softBoxLabel">Wartość netto</div><div class="softBoxValue">{{ formatMoney(invoice?.netValueOfInvoice) }} PLN</div></div>
                      <div class="softBox summaryStretchBox"><div class="softBoxLabel">Wartość VAT</div><div class="softBoxValue">{{ formatMoney(invoice?.vatValueOfInvoice) }} PLN</div></div>
                      <div class="softBox softBoxStrong summaryStretchBox"><div class="softBoxLabel">Wartość brutto</div><div class="softBoxValue softBoxValueStrong">{{ formatMoney(invoice?.valueOfInvoice) }} PLN</div></div>
                    </div>
                  </div>
                </div>

                <!-- TABLE -->
                <div class="overflow-hidden border border-slate-700 bg-slate-700 bg-opacity-30 shadow-lg" style="border-radius: 26px;">
                  <div class="flex items-center justify-between gap-3 border-b border-slate-700 px-5 py-4">
                    <div class="min-w-0">
                      <div class="text-sm font-semibold text-white">Pozycje faktury</div>
                      <div class="mt-0.5 text-xs text-slate-400">{{ invoice?.transactions?.length || 0 }} pozycji</div>
                    </div>
                    <span class="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-3 py-1.5 font-semibold text-slate-200" style="font-size: 11px;">
                      <DocumentTextIcon class="h-4 w-4" />Transakcje
                    </span>
                  </div>

                  <div class="tableScrollWrapper" ref="tableWrapper" :style="tableWrapperStyle">
                    <table class="invoiceTable divide-y divide-slate-700" ref="invoiceTable">
                      <colgroup><col ref="colLp" /><col ref="colName" /><col ref="colQty" /></colgroup>
                      <thead class="bg-slate-800 bg-opacity-80">
                        <tr class="headerGroupRow">
                          <th class="thSticky thStickyLp headerGroupCell" rowspan="2" style="left: 0;">Lp.</th>
                          <th class="thSticky thStickyName headerGroupCell" rowspan="2" :style="{ left: stickyLeft.name + 'px' }">Nazwa towaru/usługi</th>
                          <th class="thSticky thStickyQty headerGroupCell" rowspan="2" :style="{ left: stickyLeft.qty + 'px' }">Ilość (j.m.)</th>
                          <th class="headerGroupCell headerGroupSpan" colspan="5">Dane fakturowe</th>
                          <th v-if="hasOverviewTransactions" class="headerGroupCell headerGroupSpan headerGroupInfo" colspan="10">Informacje o transakcji</th>
                        </tr>
                        <tr>
                          <th class="thNormal">Cena netto</th>
                          <th class="thNormal">VAT</th>
                          <th class="thNormal">% VAT</th>
                          <th class="thNormal">Wartość netto</th>
                          <th class="thNormal">Wartość brutto</th>
                          <template v-if="hasOverviewTransactions">
                            <th class="thNormal thInfoBg thInfoBorderLeft">Data transakcji</th>
                            <th class="thNormal thInfoBg">Nazwa stacji</th>
                            <th class="thNormal thInfoBg">Imię i nazwisko</th>
                            <th class="thNormal thInfoBg">Pełny numer karty</th>
                            <th class="thNormal thInfoBg">Nr rej.</th>
                            <th class="thNormal thInfoBg">Waluta transakcji</th>
                            <th class="thNormal thInfoBg">Licznik przebiegu</th>
                            <th class="thNormal thInfoBg">Nazwa sieci</th>
                            <th class="thNormal thInfoBg">Kurs Euro</th>
                            <th class="thNormal thInfoBg">Waluta klienta</th>
                          </template>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-slate-700">
                        <tr v-for="(transaction, i) in visibleTransactions" :key="transaction.id" class="invoiceRow transition" @click="log(transaction)">
                          <td class="tdSticky tdStickyLp" :style="{ left: stickyLeft.lp + 'px' }">{{ transaction.index }}</td>
                          <td class="tdSticky tdStickyName" :style="{ left: stickyLeft.name + 'px' }">{{ transaction.name }}</td>
                          <td class="tdSticky tdStickyQty" :style="{ left: stickyLeft.qty + 'px' }">{{ transaction.quantity }}<span v-if="transaction.unit"> ({{ transaction.unit }})</span></td>
                          <td class="tdNormal">{{ formatMoney(transaction.netUnitPrice) }} PLN</td>
                          <td class="tdNormal">{{ formatMoney(transaction.vatValue) }} PLN</td>
                          <td class="tdNormal">{{ transaction.vatRate }}%</td>
                          <td class="tdNormal">{{ formatMoney(transaction.netValue) }} PLN</td>
                          <td class="tdNormal">{{ formatMoney(transaction.grossValue) }} PLN</td>
                          <template v-if="hasOverviewTransactions">
                            <template v-if="getOverviewRow(i, transaction)">
                              <td class="tdNormal tdInfoBorderLeft">
                                {{ getOverviewRow(i, transaction).TransactionDate }}
                                <span class="tdTimeSub">{{ getOverviewRow(i, transaction).TransactionTime }}</span>
                              </td>
                              <td class="tdNormal">{{ getOverviewRow(i, transaction).SiteName || '—' }}</td>
                              <td class="tdNormal">{{ getOverviewRow(i, transaction).DriverName || '—' }}</td>
                              <td class="tdNormal font-mono" style="font-size: 11px;">{{ getOverviewRow(i, transaction).CardPAN || '—' }}</td>
                              <td class="tdNormal">{{ getOverviewRow(i, transaction).VehicleRegistration || '—' }}</td>
                              <td class="tdNormal">{{ getOverviewRow(i, transaction).TransactionCurrencyCode || '—' }}</td>
                              <td class="tdNormal">{{ getOverviewRow(i, transaction).OdometerInput || '—' }}</td>
                              <td class="tdNormal">{{ getOverviewRow(i, transaction).Network || '—' }}</td>
                              <td class="tdNormal">{{ getOverviewRow(i, transaction).ColCoExchangeRate || '—' }}</td>
                              <td class="tdNormal">PLN</td>
                            </template>
                            <template v-else>
                              <td class="tdNormal tdInfoBorderLeft tdUnknown">?</td>
                              <td class="tdNormal tdUnknown">?</td><td class="tdNormal tdUnknown">?</td>
                              <td class="tdNormal tdUnknown">?</td><td class="tdNormal tdUnknown">?</td>
                              <td class="tdNormal tdUnknown">?</td><td class="tdNormal tdUnknown">?</td>
                              <td class="tdNormal tdUnknown">?</td><td class="tdNormal tdUnknown">?</td>
                              <td class="tdNormal tdUnknown">?</td>
                            </template>
                          </template>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div v-if="invoice && invoice.transactions && invoice.transactions.length > 5" class="flex items-center justify-center border-t border-slate-700 px-4 py-3">
                    <button type="button" class="btnSmall" @click="toggleAllTransactions">
                      <ChevronDownIcon class="h-4 w-4 transition" :class="showAllTransactions ? 'rotate-180' : ''" />
                      {{ showAllTransactions ? 'Pokaż mniej' : `Pokaż wszystkie pozycje (${invoice.transactions.length})` }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- FOOTER -->
            <div class="shrink-0 flex items-center justify-between gap-3 border-t border-slate-700 px-5 py-4 sm:px-6">
              <div class="text-xs text-slate-400">
                {{ invoice?.ksef?.ksefNumber ? 'Faktura zsynchronizowana z KSeF.' : 'Podgląd faktury.' }}
              </div>
              <div class="flex items-center gap-2">
                <button type="button" class="btn" @click="close" ref="cancelButtonRef">Zamknij</button>
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { ref } from "vue";
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from "@headlessui/vue";
import QrcodeVue from "qrcode.vue";
import { db } from "@/firebase/gfbconf.js";
import {
  CheckBadgeIcon,
  ArrowTopRightOnSquareIcon,
  DocumentDuplicateIcon,
  DocumentArrowDownIcon,
  LinkIcon,
  XMarkIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  BuildingOffice2Icon,
  BanknotesIcon,
  CreditCardIcon,
} from "@heroicons/vue/24/outline";

// ─── Lazy-load external libs from CDN ────────────────────────────────────────
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
}

async function ensureJsPDF() {
  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
}

async function ensureQRCode() {
  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js');
}

// Pobiera TTF z URL i zwraca base64
async function fetchFontAsBase64(url) {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Font fetch failed: ${resp.status} ${url}`);
  const buf   = await resp.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary  = '';
  // chunk po 8192 znaki żeby nie wysadzić stosu
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
  }
  return btoa(binary);
}

export default {
  components: {
    Dialog, DialogOverlay, TransitionChild, TransitionRoot, QrcodeVue,
    CheckBadgeIcon, ArrowTopRightOnSquareIcon, DocumentDuplicateIcon,
    DocumentArrowDownIcon,
    LinkIcon, XMarkIcon, ChevronDownIcon, DocumentTextIcon,
    CalendarDaysIcon, BuildingOffice2Icon, BanknotesIcon, CreditCardIcon,
  },

  props: ["modalData"],

  data() {
    return {
      invoice: null,
      showAllTransactions: false,
      stickyLeft: { lp: 0, name: 0, qty: 0 },
      _ro: null,
      pdfGenerating: false,
      primaryInvoice: null,
      primaryInvoiceLoading: false,
    };
  },

  computed: {
    visibleTransactions() {
      const t = this.invoice?.transactions || [];
      return this.showAllTransactions ? t : t.slice(0, 5);
    },
    hasOverviewTransactions() {
      return !!(this.invoice?.overviewTransactions?.length);
    },
    isCorrection() {
      const name = this.invoice?.invoiceName || '';
      return name.startsWith('KOR') || !!this.invoice?.correction;
    },
    resolvedSellerProfile() {
      // Profil sprzedawcy wyciągany z numeru faktury (spójne z logiką w Invoices.vue).
      const name = this.invoice?.invoiceName || '';
      if (name.includes('/WB/')) return 'wbtank';
      return 'lauron';
    },
    currentSeller() {
      if (this.resolvedSellerProfile === 'wbtank') {
        return { name: 'WB TANK SP. Z O.O.', nip: '8421761290', address: 'Chełmińska 3, Udorpie, 77-100 Bytów' };
      }
      return { name: 'LAURON SP. Z O.O.', nip: '8421772106', address: 'Płotowo 3A, 77-131 REKOWO' };
    },
    correctionMeta() {
      const c = this.invoice?.correction || {};
      let issueDate = '—';
      if (typeof c.dateUnix === 'number') {
        const d = new Date(c.dateUnix * 1000);
        if (!isNaN(d.getTime())) {
          issueDate = new Intl.DateTimeFormat('pl-PL', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
        }
      }
      return {
        invoiceName: c.invoiceName || '',
        reason: c.reason || '',
        issueDate,
        loadingPrimary: this.primaryInvoiceLoading,
        primaryKsefNumber: this.primaryInvoice?.ksef?.ksefNumber || null,
      };
    },
    buyerBefore() {
      const p = this.primaryInvoice || {};
      return {
        name: p.payerName || '',
        nip: p.payerNIP || '',
        address: p.payerAddress || '',
      };
    },
    hasBuyerBeforeCorrection() {
      if (!this.isCorrection || !this.primaryInvoice) return false;
      const b = this.buyerBefore;
      const cur = {
        name: this.invoice?.payerName || '',
        nip: this.invoice?.payerNIP || '',
        address: this.invoice?.payerAddress || '',
      };
      return (b.name && b.name !== cur.name)
        || (b.nip && b.nip !== cur.nip)
        || (b.address && b.address !== cur.address);
    },
    sellerBefore() {
      const name = this.primaryInvoice?.invoiceName || '';
      if (name.includes('/WB/')) {
        return { name: 'WB TANK SP. Z O.O.', nip: '8421761290', address: 'Chełmińska 3, Udorpie, 77-100 Bytów' };
      }
      return { name: 'LAURON SP. Z O.O.', nip: '8421772106', address: 'Płotowo 3A, 77-131 REKOWO' };
    },
    hasSellerBeforeCorrection() {
      if (!this.isCorrection || !this.primaryInvoice) return false;
      const before = this.sellerBefore;
      const current = this.currentSeller;
      return before.nip !== current.nip;
    },
    paymentStatusLabel() {
      const total = Number(this.invoice?.valueOfInvoice || 0);
      const paid  = Number(this.invoice?.paidAmount || 0);
      if (!!this.invoice?.paid) return "Opłacona";
      if (paid > 0 && paid < total) return "Częściowo opłacona";
      if (paid >= total && total > 0) return "Opłacona";
      return "Nieopłacona";
    },
    paymentTermLabel() {
      const date = this.formatDate(this.invoice?.paymentDate);
      const days = this.invoice?.paymentDayAmount != null ? this.invoice.paymentDayAmount : "—";
      return `${date} (${days} dni)`;
    },
    tableWrapperStyle() {
      if (!this.showAllTransactions) return { overflowY: 'hidden', maxHeight: 'none' };
      return { overflowY: 'auto', maxHeight: '400px' };
    },
  },

  mounted() {
    this.$nextTick(() => this.recalcStickyOffsets());
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this.recalcStickyOffsets());
      if (this.$refs.invoiceTable) this._ro.observe(this.$refs.invoiceTable);
    }
  },

  updated() {
    this.$nextTick(() => this.recalcStickyOffsets());
  },

  beforeUnmount() {
    if (this._ro) this._ro.disconnect();
  },

  watch: {
    modalData: {
      immediate: true, deep: true,
      handler() { this.createHardCopyOfInvoice(); },
    },
  },

  methods: {
    createHardCopyOfInvoice() {
      this.invoice = JSON.parse(JSON.stringify(this.modalData || {}));
      this.primaryInvoice = null;
      this.primaryInvoiceLoading = false;
      if (this.isCorrection) {
        this.fetchPrimaryInvoice();
      }
    },

    async fetchPrimaryInvoice() {
      const primaryName = this.invoice?.correction?.invoiceName;
      if (!primaryName) return;
      this.primaryInvoiceLoading = true;
      try {
        const snap = await db.collection('Invoices').where('invoiceName', '==', primaryName).limit(1).get();
        if (!snap.empty) {
          this.primaryInvoice = snap.docs[0].data();
        } else {
          this.primaryInvoice = null;
        }
      } catch (err) {
        console.error('Błąd pobierania faktury pierwotnej:', err);
        this.primaryInvoice = null;
      } finally {
        this.primaryInvoiceLoading = false;
      }
    },

    recalcStickyOffsets() {
      const table = this.$refs.invoiceTable;
      if (!table) return;
      const row = table.querySelector('thead tr');
      if (!row) return;
      const ths = row.querySelectorAll('th');
      const lpW   = ths[0]?.getBoundingClientRect().width || 0;
      const nameW = ths[1]?.getBoundingClientRect().width || 0;
      this.stickyLeft = { lp: 0, name: lpW, qty: lpW + nameW };
      if (ths[2]) ths[2].style.left = this.stickyLeft.qty + 'px';
    },

    toggleAllTransactions() { this.showAllTransactions = !this.showAllTransactions; },

    getOverviewRow(index, transaction) {
      const list = this.invoice?.overviewTransactions;
      if (!list?.length) return null;
      const c = list[index];
      if (!c) return null;
      if (parseFloat(c.Quantity) === parseFloat(transaction.quantity) &&
          c.transactionDateUnix === transaction.transactionDateUnix) return c;
      return null;
    },

    formatMoney(v) { const n = Number(v); return Number.isFinite(n) ? n.toFixed(2) : "0.00"; },

    formatDate(v) {
      if (!v) return "—";
      const d = new Date(v);
      if (isNaN(d.getTime())) return String(v);
      return new Intl.DateTimeFormat("pl-PL", { year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
    },

    async copyKsefNumber(v) { try { if (v) await navigator.clipboard.writeText(String(v)); } catch(e){} },
    async copyInvoiceQr(v)  { try { if (v) await navigator.clipboard.writeText(String(v)); } catch(e){} },
    log(v) { console.log(v); },
    emitClose() { this.$emit("closeModal"); },
    close() { this.emitClose(); this.open = false; },

    // ─────────────────────────────────────────────────────────────────────────
    // PDF EXPORT  –  poziomy A4, jasne kolory (druk-friendly), styl faktury
    // ─────────────────────────────────────────────────────────────────────────
    async exportPdf() {
      if (this.pdfGenerating || !this.invoice) return;
      this.pdfGenerating = true;

      try {
        await ensureJsPDF();
        await ensureQRCode();

        const { jsPDF } = window.jspdf;

        // ── Czcionka DejaVuSans – pełna obsługa latin-ext (polskie znaki) ───
        // Źródło: cdnjs (stabilne, CORS OK, zawiera unicode cmap)
        const FONT_REG_URL  = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/fonts/Roboto/Roboto-Regular.ttf';
        const FONT_BOLD_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/fonts/Roboto/Roboto-Medium.ttf';

        const [fontRegB64, fontBoldB64] = await Promise.all([
          fetchFontAsBase64(FONT_REG_URL),
          fetchFontAsBase64(FONT_BOLD_URL),
        ]);

        // QR dataURL
        let qrDataUrl = null;
        if (this.invoice?.ksef?.invoiceQr) {
          qrDataUrl = await this.generateQrDataUrl(this.invoice.ksef.invoiceQr, 240);
        }

        // ── Setup – LANDSCAPE A4 ─────────────────────────────────────────────
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

        // Rejestracja czcionki Roboto (unicode cmap, latin-ext)
        doc.addFileToVFS('Roboto-Regular.ttf', fontRegB64);
        doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
        doc.addFileToVFS('Roboto-Medium.ttf', fontBoldB64);
        doc.addFont('Roboto-Medium.ttf', 'Roboto', 'bold');

        const PW = 297, PH = 210, ML = 10, MR = 10, MT = 10;
        const CW = PW - ML - MR;
        let y = MT;

        // ── Kolory ───────────────────────────────────────────────────────────
        const WHITE    = [255, 255, 255];
        const BLACK    = [30,  30,  30 ];
        const GREY1    = [245, 245, 245];
        const HBKG     = [234, 242, 245];
        const HBKG2    = [220, 235, 242];
        const HBKG3    = [210, 230, 245];
        const BORDER   = [189, 221, 232];
        const BORDER2  = [234, 234, 234];
        const ACCTXT   = [0,   102, 153];
        const SECTBG   = [0,   84,  128];
        const MUTEDTXT = [100, 100, 100];

        // ── Helpers ──────────────────────────────────────────────────────────
        const sf = (size, style = 'normal', color = BLACK) => {
          doc.setFontSize(size);
          doc.setFont('Roboto', style);
          doc.setTextColor(...color);
        };
        const fr = (x, ry, w, h, color) => { doc.setFillColor(...color); doc.rect(x, ry, w, h, 'F'); };
        const sr = (x, ry, w, h, color = BORDER) => { doc.setDrawColor(...color); doc.setLineWidth(0.3); doc.rect(x, ry, w, h, 'S'); };
        const hl = (ly, color = BORDER, lx = ML, lw = CW) => { doc.setDrawColor(...color); doc.setLineWidth(0.3); doc.line(lx, ly, lx + lw, ly); };

        const cy = (blockTop, blockH, fsPt) => blockTop + blockH / 2 + (fsPt * 0.3528 * 0.35);

        const txt = (text, x, blockTop, blockH, fsPt, style = 'normal', color = BLACK, align = 'left') => {
          sf(fsPt, style, color);
          doc.text(String(text ?? '—'), x, cy(blockTop, blockH, fsPt), { align });
        };

        const pageBreak = (need = 12) => {
          if (y + need > PH - 12) { doc.addPage(); y = MT; }
        };

        // ═══════════════════════════════════════════════════════════════
        // NAGŁÓWEK
        // ═══════════════════════════════════════════════════════════════
        const HDR_H = 20;
        fr(ML, y, CW, HDR_H, SECTBG);

        txt('Zestawienie do faktury ' + (this.invoice.invoiceName || ''), ML + 4, y, HDR_H * 0.55, 13, 'bold', WHITE);
        if (this.invoice.invoiceDeliveryDate)
          txt('Data wystawienia: ' + this.formatDate(this.invoice.invoiceDeliveryDate), ML + 4, y + HDR_H * 0.55, HDR_H * 0.45, 8, 'normal', [200, 220, 235]);

        if (this.invoice.period)
          txt('Okres: ' + this.invoice.period, PW - MR - 4, y, HDR_H * 0.55, 8, 'normal', [200, 220, 235], 'right');

        if (this.invoice?.ksef?.ksefNumber) {
          const BW = 58, BH = 7, BX = PW - MR - BW - 2, BY = y + HDR_H * 0.55;
          doc.setFillColor(0, 160, 80);
          doc.roundedRect(BX, BY, BW, BH, 1.5, 1.5, 'F');
          txt('Zsynchronizowano z KSeF', BX + BW / 2, BY, BH, 6.5, 'bold', WHITE, 'center');
        }
        y += HDR_H + 3;

        // ═══════════════════════════════════════════════════════════════
        // 3 KOLUMNY: SPRZEDAWCA | NABYWCA | PŁATNOŚĆ
        // ═══════════════════════════════════════════════════════════════
        const C3W = (CW - 6) / 3;
        const C1X = ML, C2X = ML + C3W + 3, C3X = ML + 2 * (C3W + 3);

        const CH  = 8;
        const LH  = 7;
        const PTV = 3.5;

        const B1  = CH + PTV + 5 * LH + PTV;
        const B3  = CH + PTV + 4 * LH + 3 + 2 * LH + PTV;
        const RH  = 9;

        sr(C1X, y, C3W, B1, BORDER);
        sr(C2X, y, C3W, B1, BORDER);
        sr(C3X, y, C3W, B3 + RH, BORDER);

        // ── Kol1: SPRZEDAWCA ─────────────────────────────────────────
        fr(C1X, y, C3W, CH, HBKG2);
        txt('SPRZEDAWCA', C1X + 3, y, CH, 7, 'bold', ACCTXT);
        hl(y + CH, BORDER, C1X, C3W);

        let ry = y + CH + PTV;
        txt('LAURON SP. Z O.O.',               C1X + 3,    ry,      LH, 8.5, 'bold',   BLACK);
        txt('NIP:',                             C1X + 3,    ry+LH,   LH, 7.5, 'normal', MUTEDTXT);
        txt('8421772106',                       C1X+C3W-3,  ry+LH,   LH, 7.5, 'normal', BLACK, 'right');
        txt('Adres:',                           C1X + 3,    ry+2*LH, LH, 7.5, 'normal', MUTEDTXT);
        txt('Płotowo 3A, 77-131 REKOWO',        C1X+C3W-3,  ry+2*LH, LH, 7.5, 'normal', BLACK, 'right');
        txt('Bank:',                            C1X + 3,    ry+3*LH, LH, 7.5, 'normal', MUTEDTXT);
        txt('PKO BP',                           C1X+C3W-3,  ry+3*LH, LH, 7.5, 'normal', BLACK, 'right');
        txt('Konto:',                           C1X + 3,    ry+4*LH, LH, 7,   'normal', MUTEDTXT);
        txt('42 1020 1068 0000 1102 0371 8830', C1X+C3W-3,  ry+4*LH, LH, 7,   'normal', BLACK, 'right');

        // ── Kol2: NABYWCA ─────────────────────────────────────────────
        fr(C2X, y, C3W, CH, HBKG2);
        txt('NABYWCA', C2X + 3, y, CH, 7, 'bold', ACCTXT);
        hl(y + CH, BORDER, C2X, C3W);

        ry = y + CH + PTV;
        txt(this.invoice.payerName || '—',                            C2X + 3,   ry,      LH, 8.5, 'bold',   BLACK);
        txt('NIP:',                                                    C2X + 3,   ry+LH,   LH, 7.5, 'normal', MUTEDTXT);
        txt(this.invoice.payerNIP || '—',                             C2X+C3W-3, ry+LH,   LH, 7.5, 'normal', BLACK, 'right');
        txt('Adres:',                                                  C2X + 3,   ry+2*LH, LH, 7.5, 'normal', MUTEDTXT);
        txt((this.invoice.payerAddress||'—').replace(/\n/g,', '),     C2X+C3W-3, ry+2*LH, LH, 7.5, 'normal', BLACK, 'right');

        // ── Kol3: PŁATNOŚĆ I PODSUMOWANIE ────────────────────────────
        fr(C3X, y, C3W, CH, HBKG2);
        txt('PŁATNOŚĆ I PODSUMOWANIE', C3X + 3, y, CH, 7, 'bold', ACCTXT);
        hl(y + CH, BORDER, C3X, C3W);

        ry = y + CH + PTV;
        const pmtRows = [
          ['Status',             this.paymentStatusLabel],
          ['Sposób płatności',   'przelew'],
          ['Termin płatności',   this.paymentTermLabel],
          ['Opłacono',           this.formatMoney(this.invoice.paidAmount) + ' PLN'],
        ];
        pmtRows.forEach(([k, v], i) => {
          txt(k + ':', C3X + 3,    ry+i*LH, LH, 7.5, 'normal', MUTEDTXT);
          txt(String(v), C3X+C3W-3, ry+i*LH, LH, 7.5, 'normal', BLACK, 'right');
        });

        const sepY = ry + 4 * LH + 1;
        hl(sepY, BORDER2, C3X + 2, C3W - 4);

        const r2 = sepY + 3;
        txt('Wartość netto:', C3X + 3,   r2,    LH, 7.5, 'normal', MUTEDTXT);
        txt(this.formatMoney(this.invoice.netValueOfInvoice) + ' PLN', C3X+C3W-3, r2,    LH, 7.5, 'normal', BLACK, 'right');
        txt('Wartość VAT:',  C3X + 3,   r2+LH, LH, 7.5, 'normal', MUTEDTXT);
        txt(this.formatMoney(this.invoice.vatValueOfInvoice) + ' PLN', C3X+C3W-3, r2+LH, LH, 7.5, 'normal', BLACK, 'right');

        const RY = y + B3;
        hl(RY, BORDER, C3X, C3W);
        fr(C3X, RY, C3W, RH, SECTBG);
        txt('RAZEM DO ZAPŁATY:', C3X + 3,   RY, RH, 7.5, 'bold', [200, 220, 235]);
        txt(this.formatMoney(this.invoice.valueOfInvoice) + ' PLN', C3X+C3W-3, RY, RH, 10, 'bold', WHITE, 'right');

        y += Math.max(B1, B3 + RH) + 4;

        // ═══════════════════════════════════════════════════════════════
        // KSEF
        // ═══════════════════════════════════════════════════════════════
        if (this.invoice?.ksef?.ksefNumber) {
          pageBreak(38);
          const QS = 24, QP = 3.5;
          const KH = CH + QP + QS + QP;

          sr(ML, y, CW, KH, BORDER);
          fr(ML, y, CW, CH, HBKG2);
          txt('FAKTURA DOSTĘPNA W KSEF', ML + 3, y, CH, 7, 'bold', [0, 130, 60]);
          hl(y + CH, BORDER, ML, CW);

          if (qrDataUrl)
            doc.addImage(qrDataUrl, 'PNG', ML + QP, y + CH + QP, QS, QS);

          const kx = qrDataUrl ? ML + QP + QS + QP + 2 : ML + 4;
          const kw = CW - (qrDataUrl ? QP + QS + QP + 6 : 6);
          const hk = kw / 2 - 4;

          const kTL = y + CH + QP;
          const kTV = y + CH + QP + LH;
          txt('Numer KSeF:',      kx,      kTL, LH, 7, 'normal', MUTEDTXT);
          txt(this.invoice.ksef.ksefNumber || '—', kx, kTV, LH, 7, 'normal', BLACK);
          txt('Data wystawienia:', kx+hk+8, kTL, LH, 7, 'normal', MUTEDTXT);
          txt(this.formatDate(this.invoice.invoiceDeliveryDate), kx+hk+8, kTV, LH, 7, 'normal', BLACK);

          if (this.invoice.ksef.invoiceQr) {
            const linkT = y + CH + QP + QS - LH;
            txt('Link:', kx, linkT, LH, 7, 'normal', MUTEDTXT);
            sf(6.5, 'normal', ACCTXT);
            const ll = doc.splitTextToSize(this.invoice.ksef.invoiceQr, kw - 10);
            doc.text(ll[0], kx + 8, cy(linkT, LH, 6.5));
          }

          y += KH + 4;
        }

        // ═══════════════════════════════════════════════════════════════
        // TABELA POZYCJI
        // ═══════════════════════════════════════════════════════════════
        pageBreak(20);

        const transactions = this.invoice.transactions || [];
        const hasOv = this.hasOverviewTransactions;

        const BASE_COLS = [
          { k: 'index',        h: 'Lp.',                 w: 6,  a: 'center' },
          { k: 'name',         h: 'Nazwa towaru/usługi', w: 36, a: 'left'   },
          { k: 'qty',          h: 'Ilość (j.m.)',         w: 14, a: 'center' },
          { k: 'netUnitPrice', h: 'Cena netto',           w: 14, a: 'right'  },
          { k: 'vatValue',     h: 'Wartość VAT',          w: 14, a: 'right'  },
          { k: 'vatRate',      h: '%VAT',                 w:  8, a: 'center' },
          { k: 'netValue',     h: 'W.netto',              w: 16, a: 'right'  },
          { k: 'grossValue',   h: 'W.brutto',             w: 16, a: 'right'  },
        ];
        const OV_COLS = hasOv ? [
          { k: 'txDate',  h: 'Data/Czas',       w: 27, a: 'center' },
          { k: 'site',    h: 'Nazwa stacji',     w: 30, a: 'left'   },
          { k: 'driver',  h: 'Imię i nazwisko',  w: 13, a: 'left'   },
          { k: 'card',    h: 'Nr karty',         w: 22, a: 'left'   },
          { k: 'reg',     h: 'Nr rej.',          w: 13, a: 'center' },
          { k: 'curr',    h: 'Waluta',           w:  9, a: 'center' },
          { k: 'odo',     h: 'Przebieg',         w: 13, a: 'right'  },
          { k: 'net',     h: 'Sieć',             w: 12, a: 'center' },
          { k: 'eur',     h: 'Kurs EUR',         w: 12, a: 'right'  },
          { k: 'wcurr',   h: 'Wal.kl.',          w:  9, a: 'center' },
        ] : [];

        const ALL_COLS  = [...BASE_COLS, ...OV_COLS];
        const totalColW = ALL_COLS.reduce((s, c) => s + c.w, 0);
        const SFc       = CW / totalColW;
        const COLS      = ALL_COLS.map(c => ({ ...c, w: parseFloat((c.w * SFc).toFixed(3)) }));

        const TH2H  = hasOv ? 5.5 : 0;
        const THH   = 6.5;
        const RWH   = 5.5;
        const SH    = 8;
        const FDATA = 5.8;
        const FHEAD = 5.5;
        const bcW   = BASE_COLS.reduce((s, _, i) => s + COLS[i].w, 0);

        const drawTH = (first = false) => {
          if (first) {
            fr(ML, y, CW, SH, SECTBG);
            txt('ZAWARTE POZYCJE', ML + 3, y, SH, 8, 'bold', WHITE);
            txt(transactions.length + ' pozycji', PW-MR-3, y, SH, 7.5, 'normal', [200, 220, 235], 'right');
            y += SH;
          }
          if (hasOv) {
            const ovX = ML + bcW, ovW = CW - bcW;
            fr(ML,  y, bcW, TH2H, HBKG);   sr(ML,  y, bcW, TH2H, BORDER);
            fr(ovX, y, ovW, TH2H, HBKG3);  sr(ovX, y, ovW, TH2H, BORDER);
            txt('DANE FAKTUROWE',          ML+bcW/2,  y, TH2H, FHEAD, 'bold', ACCTXT, 'center');
            txt('INFORMACJE O TRANSAKCJI', ovX+ovW/2, y, TH2H, FHEAD, 'bold', ACCTXT, 'center');
            y += TH2H;
          }
          let hx = ML;
          COLS.forEach((col, ci) => {
            const isOv = hasOv && ci >= BASE_COLS.length;
            fr(hx, y, col.w, THH, isOv ? HBKG3 : HBKG);
            sr(hx, y, col.w, THH, BORDER);
            const tx = col.a === 'right' ? hx+col.w-1.2 : col.a === 'center' ? hx+col.w/2 : hx+1.2;
            txt(col.h, tx, y, THH, FHEAD, 'bold', isOv ? [0,80,130] : [60,80,100], col.a === 'left' ? 'left' : col.a);
            hx += col.w;
          });
          y += THH;
        };

        drawTH(true);

        transactions.forEach((tr, idx) => {
          if (y + RWH > PH - 12) { doc.addPage(); y = MT; drawTH(false); }

          const ov = hasOv ? this.getOverviewRow(idx, tr) : null;
          fr(ML, y, CW, RWH, idx % 2 === 0 ? WHITE : GREY1);

          const data = {
            index:        String(tr.index),
            name:         tr.name || '—',
            qty:          (tr.quantity !== undefined ? tr.quantity : '') + (tr.unit ? ' ' + tr.unit : ''),
            netUnitPrice: this.formatMoney(tr.netUnitPrice),
            vatValue:     this.formatMoney(tr.vatValue),
            vatRate:      (tr.vatRate || '0') + '%',
            netValue:     this.formatMoney(tr.netValue),
            grossValue:   this.formatMoney(tr.grossValue),
            txDate:  ov ? ((ov.TransactionDate||'') + (ov.TransactionTime ? ' '+ov.TransactionTime : '')) : '—',
            site:    ov ? (ov.SiteName            || '—') : '—',
            driver:  ov ? (ov.DriverName          || '—') : '—',
            card:    ov ? (ov.CardPAN             || '—') : '—',
            reg:     ov ? (ov.VehicleRegistration || '—') : '—',
            curr:    ov ? (ov.TransactionCurrencyCode || '—') : '—',
            odo:     ov ? String(ov.OdometerInput || '—') : '—',
            net:     ov ? (ov.Network             || '—') : '—',
            eur:     ov ? String(ov.ColCoExchangeRate || '—') : '—',
            wcurr:   'PLN',
          };

          let cx = ML;
          COLS.forEach((col, ci) => {
            const isOv   = hasOv && ci >= BASE_COLS.length;
            const isName = ci === 1;
            const color  = isOv ? [60,80,110] : (isName ? BLACK : [50,50,50]);
            const raw    = String(data[col.k] ?? '—');
            const maxW   = col.w - 2.4;
            const tx     = col.a === 'right' ? cx+col.w-1.2 : col.a === 'center' ? cx+col.w/2 : cx+1.2;

            let fSize = FDATA;
            sf(fSize, isName ? 'bold' : 'normal', color);
            while (doc.getTextWidth(raw) > maxW && fSize > 4.5) {
              fSize -= 0.3;
              sf(fSize, isName ? 'bold' : 'normal', color);
            }

            doc.text(raw, tx, cy(y, RWH, fSize), { align: col.a === 'left' ? 'left' : col.a });
            doc.setDrawColor(...BORDER2); doc.setLineWidth(0.2); doc.rect(cx, y, col.w, RWH, 'S');
            cx += col.w;
          });
          y += RWH;
        });

        hl(y, BORDER);
        y += 3;

        // ── Stopka każdej strony ─────────────────────────────────────
        const totalPages = doc.internal.getNumberOfPages();
        for (let p = 1; p <= totalPages; p++) {
          doc.setPage(p);
          hl(PH - 8, BORDER);
          sf(6.5, 'normal', MUTEDTXT);
          doc.text('Lauron Sp. z o.o.  |  Płotowo 3A, 77-131 REKOWO  |  NIP: 8421772106', ML, PH - 4);
          doc.text('Strona ' + p + ' / ' + totalPages, PW - MR, PH - 4, { align: 'right' });
        }

        const fname = (this.invoice.invoiceName || 'zestawienie').replace(/\//g, '_') + '_zestawienie.pdf';
        doc.save(fname);

      } catch (err) {
        console.error('PDF error:', err);
        alert('Nie udało się wygenerować PDF. Szczegóły w konsoli.');
      } finally {
        this.pdfGenerating = false;
      }
    },

    /**
     * Generuje QR jako PNG dataURL przez ukryty div + QRCode.js
     */
    generateQrDataUrl(text, size = 220) {
      return new Promise(resolve => {
        try {
          const div = document.createElement('div');
          div.style.cssText = 'position:fixed;left:-9999px;top:-9999px;';
          document.body.appendChild(div);
          // eslint-disable-next-line no-undef
          new QRCode(div, { text, width: size, height: size, colorDark: '#000', colorLight: '#fff', correctLevel: QRCode.CorrectLevel.M });
          setTimeout(() => {
            const canvas = div.querySelector('canvas');
            const url = canvas ? canvas.toDataURL('image/png') : null;
            document.body.removeChild(div);
            resolve(url);
          }, 400);
        } catch(e) { resolve(null); }
      });
    },
  },

  setup() {
    const open = ref(true);
    return { open };
  },
};
</script>

<style scoped>
.backdrop-soft { backdrop-filter: blur(3px); }

/* ─── Soft mini rows ─────────────────────────────────────────── */
.softMiniRow { display: grid; grid-template-columns: 48px minmax(0, 1fr); gap: 10px; align-items: center; }
.softMiniRow--top { align-items: start; }
.softMiniKey { font-size: 11px; font-weight: 600; color: rgba(148,163,184,1); }
.softMiniVal { font-size: 12px; line-height: 1.6; font-weight: 600; color: rgba(226,232,240,1); word-break: break-word; }

/* ─── Buttons ────────────────────────────────────────────────── */
.iconBtn {
  height: 40px; width: 40px; border-radius: 16px;
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  border: 1px solid rgba(100,116,139,0.65); background: rgba(51,65,85,0.75);
  transition: 180ms ease; cursor: pointer;
}
.iconBtn:hover { background: rgba(71,85,105,0.95); }
.iconBtn:disabled { opacity: 0.5; cursor: not-allowed; }

.btn, .primaryBtn, .btnSmall { white-space: nowrap; }
.btnIcon { flex-shrink: 0; }
.btnLabel { white-space: nowrap; }

.btn {
  height: 40px; padding: 0 16px; border-radius: 16px;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  border: 1px solid rgba(100,116,139,0.65); background: rgba(51,65,85,0.78);
  color: #e2e8f0; font-size: 12px; font-weight: 600; transition: 180ms ease;
}
.btn:hover { background: rgba(71,85,105,0.95); }

.primaryBtn {
  height: 40px; padding: 0 16px; border-radius: 16px;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  border: 1px solid rgba(59,130,246,0.45); background: rgba(37,99,235,0.18);
  color: #dbeafe; font-size: 12px; font-weight: 700; transition: 180ms ease;
}
.primaryBtn:hover { background: rgba(37,99,235,0.28); }

.btnSmall {
  height: 36px; padding: 0 14px; border-radius: 14px;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  border: 1px solid rgba(100,116,139,0.65); background: rgba(51,65,85,0.68);
  color: #e2e8f0; font-size: 12px; font-weight: 600; transition: 180ms ease;
}
.btnSmall:hover { background: rgba(71,85,105,0.92); }

/* ─── PDF spinner ────────────────────────────────────────────── */
.pdfSpinner {
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid rgba(148,163,184,0.3); border-top-color: #e2e8f0;
  border-radius: 50%; animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ─── Soft boxes ─────────────────────────────────────────────── */
.softBox { border-radius: 18px; border: 1px solid rgba(100,116,139,0.45); background: rgba(15,23,42,0.22); padding: 12px 14px; }
.softBoxStrong { border-color: rgba(59,130,246,0.35); background: rgba(30,41,59,0.45); }
.softBoxLabel { font-size: 11px; font-weight: 600; color: rgba(148,163,184,1); }
.softBoxValue { margin-top: 6px; font-size: 12px; font-weight: 600; color: rgba(226,232,240,1); line-height: 1.6; }
.softBoxValueStrong { font-size: 14px; color: #fff; }

/* ─── KSeF / misc ────────────────────────────────────────────── */
.actionCol { width: 220px; }
.qrWrap { display: flex; justify-content: center; }
.qrInner { display: inline-flex; }
.summaryCard { display: flex; flex-direction: column; }
.summaryCardBody { flex: 1; display: flex; flex-direction: column; }
.summaryStretchBox { flex: 1; display: flex; flex-direction: column; justify-content: center; }

/* ─── TABLE SCROLL WRAPPER ───────────────────────────────────── */
.tableScrollWrapper { width: 100%; overflow-x: auto; }
.invoiceTable { width: max-content; min-width: 100%; border-collapse: separate; border-spacing: 0; }
.invoiceTable thead { position: sticky; top: 0; z-index: 10; }

/* ─── HEADER GROUP ROW ───────────────────────────────────────── */
.headerGroupCell {
  padding: 10px 12px 6px; font-size: 11px; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase; color: rgba(148,163,184,1); background: #0f172a;
  white-space: nowrap; border-bottom: 1px solid rgba(51,65,85,0.8);
  text-align: center; vertical-align: middle;
}
.headerGroupSpan { border-left: 1px solid rgba(51,65,85,0.8); }
.headerGroupInfo { color: rgba(125,211,252,0.9); border-left: 2px solid rgba(56,189,248,0.5); background: #0a1e32; }

/* ─── NORMAL TH ──────────────────────────────────────────────── */
.thNormal {
  padding: 6px 12px 6px; font-size: 11px; font-weight: 600; letter-spacing: 0.04em;
  text-transform: uppercase; color: rgba(148,163,184,1); background: #0f172a;
  white-space: nowrap; text-align: left; vertical-align: bottom;
  border-bottom: 1px solid rgba(51,65,85,0.8);
}
.thNormal.thInfoBg { background: #0a1e32; }
.thInfoBorderLeft { border-left: 2px solid rgba(56,189,248,0.5); }

/* ─── STICKY COLUMNS ─────────────────────────────────────────── */
.thStickyLp, .tdStickyLp { position: sticky; left: 0; z-index: 4; min-width: 44px; width: 44px; }
.thStickyName, .tdStickyName { position: sticky; z-index: 4; min-width: 160px; width: 200px; max-width: 220px; }
.thStickyQty, .tdStickyQty { position: sticky; z-index: 4; min-width: 90px; width: 90px; border-right: 2px solid rgba(71,85,105,0.9); }

.thSticky {
  padding: 10px 12px; font-size: 11px; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase; color: rgba(148,163,184,1); background: #0f172a;
  white-space: nowrap; text-align: left; border-bottom: 1px solid rgba(51,65,85,0.8); vertical-align: middle;
}

/* ─── TD ─────────────────────────────────────────────────────── */
.tdNormal { white-space: nowrap; padding: 12px 12px; font-size: 12px; font-weight: 600; color: rgba(226,232,240,1); }
.tdInfoBorderLeft { border-left: 2px solid rgba(56,189,248,0.3); }
.tdSticky { white-space: nowrap; padding: 12px 12px; font-size: 12px; font-weight: 600; color: #fff; background: #1e293b; }
.tdUnknown { color: rgba(100,116,139,0.8); text-align: center; }
.tdTimeSub { display: block; font-size: 10px; font-weight: 500; color: rgba(148,163,184,0.8); margin-top: 1px; }

/* ─── ROW HOVER ──────────────────────────────────────────────── */
.invoiceRow { background: rgba(51,65,85,0.20); }
.invoiceRow:hover { background: rgba(51,65,85,0.40); }
.invoiceRow:hover .tdSticky { background: #263348; }

/* ─── RESPONSIVE ─────────────────────────────────────────────── */
@media (max-width: 1279px) { .actionCol { width: 100%; } }
@media (max-width: 767px) {
  .actionButtons { width: 100%; display: grid; grid-template-columns: 1fr; }
  .actionButtons .btn, .actionButtons .primaryBtn { width: 100%; min-height: 44px; justify-content: center; }
  .qrWrap { width: 100%; justify-content: center; }
  .qrInner { padding-left: 8px; padding-right: 8px; }
}
@media (min-width: 768px) and (max-width: 1279px) {
  .actionButtons { width: 100%; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .actionButtons .btn, .actionButtons .primaryBtn { width: 100%; min-height: 44px; padding-left: 12px; padding-right: 12px; }
  .actionButtons .btnLabel { overflow: hidden; text-overflow: ellipsis; }
}
</style>